import React from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope, Users, Calendar, Shield, Video, CreditCard,
  UserPlus, CheckCircle, HelpCircle,
} from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import useDoctorRegistrationOpen from '../hooks/useDoctorRegistrationOpen';
import usePricing from '../hooks/usePricing';
import { describeSurchargeWhen, formatCOP, PRICING_UNAVAILABLE_TEXT } from '../config/pricing';
import { HEALTH_GUIDES } from '../config/content';

function HomePage() {
  useSeoMeta(
    'DocEnLinea | Consulta médica online con médicos colombianos',
    'Plataforma de telemedicina en Colombia: agenda tu consulta con un médico general verificado y atiéndete por videollamada o chat, con fórmula médica digital.',
    '/'
  );
  // Con el registro de médicos cerrado (panel admin) se ocultan los enlaces a /medicos.
  const { open: doctorRegistrationOpen } = useDoctorRegistrationOpen();
  // Tarifa vigente definida por el backend. Sin respuesta no se muestra ningún valor.
  const { pricing } = usePricing();
  const surchargeWhen = describeSurchargeWhen(pricing);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Navbar */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Stethoscope className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DocEnLinea</span>
            </div>
            <div className="hidden md:flex items-center gap-6 mr-4">
              <Link to="/pacientes" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Para pacientes</Link>
              {doctorRegistrationOpen && (
                <Link to="/medicos" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Para médicos</Link>
              )}
              <a href="#como-funciona" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Cómo funciona</a>
              <a href="#precios" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Precios</a>
              <Link to="/faq" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Preguntas frecuentes</Link>
              <Link to="/soporte" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Soporte</Link>
            </div>
            <div className="flex gap-4">
              <Link to="/login" className="btn-secondary">
                Iniciar Sesión
              </Link>
              <Link to="/register" className="btn-primary">
                Registrarse
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Consultas Médicas Online
            <br />
            <span className="text-primary-600">Desde la Comodidad de tu Casa</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            DocEnLinea te conecta con médicos generales colombianos por videollamada y chat.
            Recibe atención médica de calidad, rápida y segura, sin salir de casa.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register" className="btn-primary text-lg px-8 py-3">
              Comenzar Ahora
            </Link>
            <a href="#como-funciona" className="btn-secondary text-lg px-8 py-3">
              Ver Cómo Funciona
            </a>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-20">
          <div className="card text-center hover:shadow-xl transition-shadow">
            <Users className="h-12 w-12 text-primary-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Médicos Colombianos</h3>
            <p className="text-gray-600">
              Profesionales de medicina general habilitados para ejercer en Colombia
            </p>
          </div>

          <div className="card text-center hover:shadow-xl transition-shadow">
            <Video className="h-12 w-12 text-primary-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Videollamada y Chat</h3>
            <p className="text-gray-600">
              Atiéndete desde tu celular o computador, estés donde estés
            </p>
          </div>

          <div className="card text-center hover:shadow-xl transition-shadow">
            <Shield className="h-12 w-12 text-primary-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Datos Protegidos</h3>
            <p className="text-gray-600">
              Tu información se trata de forma confidencial (Ley 1581 de 2012)
            </p>
          </div>
        </div>

        {/* Landing pages segmentadas: pacientes / médicos (esta última solo
            con el registro de médicos abierto) */}
        <div className={doctorRegistrationOpen ? 'grid md:grid-cols-2 gap-8 mt-20' : 'max-w-xl mx-auto mt-20'}>
          <Link
            to="/pacientes"
            className="card block hover:shadow-xl transition-shadow border-2 border-transparent hover:border-primary-200"
          >
            <Users className="h-10 w-10 text-primary-600 mb-3" />
            <h3 className="text-xl font-semibold mb-2">¿Eres paciente?</h3>
            <p className="text-gray-600 mb-4">
              Descubre quién te atiende, cuánto cuesta, si recibes receta y qué enfermedades
              atendemos.
            </p>
            <span className="text-primary-600 font-medium">Ver información para pacientes &rarr;</span>
          </Link>

          {doctorRegistrationOpen && (
          <Link
            to="/medicos"
            className="card hover:shadow-xl transition-shadow border-2 border-transparent hover:border-primary-200"
          >
            <Stethoscope className="h-10 w-10 text-primary-600 mb-3" />
            <h3 className="text-xl font-semibold mb-2">¿Eres médico?</h3>
            <p className="text-gray-600 mb-4">
              Conoce cómo funciona la plataforma, tus ingresos, tu agenda y tus
              responsabilidades como profesional independiente.
            </p>
            <span className="text-primary-600 font-medium">Ver información para médicos &rarr;</span>
          </Link>
          )}
        </div>

        {/* Como Funciona */}
        <div id="como-funciona" className="mt-32 scroll-mt-20">
          <h2 className="text-4xl font-bold text-center mb-16">¿Cómo Funciona?</h2>
          <div className="grid md:grid-cols-4 gap-10">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">1. Regístrate</h3>
              <p className="text-gray-600 text-sm">
                Crea tu cuenta de paciente en menos de 2 minutos.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">2. Agenda tu cita</h3>
              <p className="text-gray-600 text-sm">
                Elige el médico y el horario que mejor te quede.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">3. Paga seguro</h3>
              <p className="text-gray-600 text-sm">
                Realiza el pago en línea de forma segura al agendar.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">4. Consulta online</h3>
              <p className="text-gray-600 text-sm">
                Conéctate a tu videollamada. Recibe diagnóstico y fórmula digital.
              </p>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            Te enviaremos la confirmación por correo y un recordatorio por WhatsApp 5 minutos antes de tu cita.
          </p>
        </div>

        {/* Precios */}
        <div id="precios" className="mt-32 scroll-mt-20">
          <h2 className="text-4xl font-bold text-center mb-4">Precios Claros</h2>
          <p className="text-center text-gray-600 mb-12 max-w-xl mx-auto">
            Sin sorpresas ni mensualidades. Pagas únicamente por la consulta que agendas.
          </p>
          <div className="max-w-md mx-auto">
            <div className="card text-center border-2 border-primary-200 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Consulta de Medicina General
              </h3>
              {pricing ? (
                <>
                  <div className="text-5xl font-bold text-primary-600 my-4">
                    {formatCOP(pricing.basePrice)}
                    {surchargeWhen && <span className="align-super text-3xl">*</span>}
                  </div>
                  <p className="text-gray-500 text-sm mb-4">
                    por consulta{surchargeWhen ? ' en horario diurno' : ''}
                  </p>
                  {surchargeWhen && (
                    <p className="text-sm text-gray-600 bg-primary-50 rounded-lg px-4 py-3 mb-6" data-testid="pricing-surcharge">
                      * Las consultas {surchargeWhen} tienen un recargo
                      de {formatCOP(pricing.nightSurcharge)} que se suma al valor base:
                      total <strong>{formatCOP(pricing.nightTotal)}</strong>.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-gray-600 my-6">{PRICING_UNAVAILABLE_TEXT}</p>
              )}
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Videollamada con médico general</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Fórmula médica y recomendaciones digitales</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Recordatorio por WhatsApp antes de tu cita</span>
                </li>
              </ul>
              <Link to="/register" className="btn-primary w-full py-3 inline-block">
                Agendar mi consulta
              </Link>
            </div>
          </div>
        </div>

        {/* CTA a FAQ */}
        <div className="mt-32 bg-primary-600 rounded-2xl p-12 text-white text-center">
          <HelpCircle className="h-12 w-12 mx-auto mb-4 text-primary-100" />
          <h2 className="text-3xl font-bold mb-4">¿Tienes preguntas?</h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Resolvemos las dudas más comunes sobre cómo funciona la plataforma, los pagos,
            la confidencialidad y más.
          </p>
          <Link
            to="/faq"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors inline-block"
          >
            Ver Preguntas Frecuentes
          </Link>
        </div>

        {/* CTA Final */}
        <div className="mt-32 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Listo para tu Primera Consulta?</h2>
          <Link to="/register" className="btn-primary text-lg px-8 py-3 inline-block">
            Registrarse Gratis
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-32 py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Stethoscope className="h-8 w-8" />
            <span className="ml-2 text-xl font-bold">DocEnLinea</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/pacientes" className="hover:text-white">Para pacientes</Link>
            {doctorRegistrationOpen && <Link to="/medicos" className="hover:text-white">Para médicos</Link>}
            <a href="#como-funciona" className="hover:text-white">Cómo funciona</a>
            <a href="#precios" className="hover:text-white">Precios</a>
            <Link to="/faq" className="hover:text-white">Preguntas Frecuentes</Link>
            <Link to="/soporte" className="hover:text-white">Soporte</Link>
            <Link to="/login" className="hover:text-white">Iniciar Sesión</Link>
            <Link to="/register" className="hover:text-white">Registrarse</Link>
          </div>
          <nav aria-label="Guías de salud" className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <span className="text-gray-500">Guías de salud:</span>
            {HEALTH_GUIDES.map(({ href, label }) => (
              <a key={href} href={href} className="hover:text-white">{label}</a>
            ))}
          </nav>
          <p className="text-gray-400">
            Telemedicina de calidad en Colombia
          </p>
          <p className="text-gray-500 text-xs mt-4 max-w-xl mx-auto">
            DocEnLinea NO es un servicio de emergencias. Ante una urgencia vital, llama al 123
            o acude al servicio de urgencias más cercano.
          </p>
          <p className="text-gray-500 text-sm mt-4">
            © 2025 DocEnLinea. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
